import Phaser from 'phaser';
import { TILE_W, TILE_H } from './config';

export type TerrainKind =
  | 'grass' | 'dirt' | 'stone' | 'water' | 'wall' | 'wood'
  | 'mud' | 'sand' | 'ash'
  | 'ramp_n' | 'ramp_e' | 'ramp_s' | 'ramp_w';

export interface TerrainStyle {
  fill: number;
  edge: number;
  walkable: boolean;
  costMultiplier: number;
  /** If set, this tile is a ramp facing this side and changes elevation. */
  ramp?: 'n' | 'e' | 's' | 'w';
}

export const TERRAIN: Record<TerrainKind, TerrainStyle> = {
  grass: { fill: 0x3e6b34, edge: 0x244a1d, walkable: true,  costMultiplier: 1.0 },
  dirt:  { fill: 0x6e5436, edge: 0x4a3722, walkable: true,  costMultiplier: 1.1 },
  stone: { fill: 0x5b6168, edge: 0x363b3f, walkable: true,  costMultiplier: 1.3 },
  water: { fill: 0x2c4d6b, edge: 0x18324c, walkable: false, costMultiplier: 99 },
  wall:  { fill: 0x2a2521, edge: 0x14110e, walkable: false, costMultiplier: 99 },
  wood:  { fill: 0x8a5a2b, edge: 0x4a2f17, walkable: true,  costMultiplier: 1.0 },
  mud:   { fill: 0x4a3a26, edge: 0x281d10, walkable: true,  costMultiplier: 1.6 },
  sand:  { fill: 0xc4a86c, edge: 0x8a7440, walkable: true,  costMultiplier: 1.1 },
  ash:   { fill: 0x4a4546, edge: 0x2b282a, walkable: true,  costMultiplier: 1.2 },
  ramp_n: { fill: 0x6e5436, edge: 0x4a3722, walkable: true, costMultiplier: 1.2, ramp: 'n' },
  ramp_e: { fill: 0x6e5436, edge: 0x4a3722, walkable: true, costMultiplier: 1.2, ramp: 'e' },
  ramp_s: { fill: 0x6e5436, edge: 0x4a3722, walkable: true, costMultiplier: 1.2, ramp: 's' },
  ramp_w: { fill: 0x6e5436, edge: 0x4a3722, walkable: true, costMultiplier: 1.2, ramp: 'w' },
};

/** Pre-render terrain textures (diamonds + wall cubes + ramp wedges). */
export function ensureTerrainTextures(scene: Phaser.Scene): void {
  for (const kind of Object.keys(TERRAIN) as TerrainKind[]) {
    const key = `terrain_${kind}`;
    if (scene.textures.exists(key)) continue;

    const style = TERRAIN[kind];
    const g = scene.add.graphics({ x: 0, y: 0 });
    g.fillStyle(style.fill, 1);
    g.lineStyle(1, style.edge, 1);

    g.beginPath();
    g.moveTo(TILE_W / 2, 0);
    g.lineTo(TILE_W, TILE_H / 2);
    g.lineTo(TILE_W / 2, TILE_H);
    g.lineTo(0, TILE_H / 2);
    g.closePath();
    g.fillPath();
    g.strokePath();

    if (kind === 'wall') {
      g.fillStyle(0x3a322a, 1);
      g.fillRect(TILE_W / 4, -TILE_H / 2, TILE_W / 2, TILE_H);
      g.lineStyle(1, style.edge, 1);
      g.strokeRect(TILE_W / 4, -TILE_H / 2, TILE_W / 2, TILE_H);
    }

    // Ramp visual: an arrow-like wedge on the diamond pointing in the ramp direction.
    if (style.ramp) {
      g.fillStyle(0xffd54a, 0.45);
      g.lineStyle(1, 0xffd54a, 0.6);
      const cx = TILE_W / 2;
      const cy = TILE_H / 2;
      const tip: Record<'n' | 'e' | 's' | 'w', [number, number, number, number, number, number]> = {
        n: [cx, 2,           cx - 8, cy,      cx + 8, cy],
        s: [cx, TILE_H - 2,  cx - 8, cy,      cx + 8, cy],
        e: [TILE_W - 2, cy,  cx,     cy - 6,  cx,     cy + 6],
        w: [2, cy,           cx,     cy - 6,  cx,     cy + 6],
      };
      const [x1, y1, x2, y2, x3, y3] = tip[style.ramp];
      g.beginPath();
      g.moveTo(x1, y1); g.lineTo(x2, y2); g.lineTo(x3, y3);
      g.closePath();
      g.fillPath(); g.strokePath();
    }

    g.generateTexture(key, TILE_W, kind === 'wall' ? TILE_H + TILE_H / 2 : TILE_H);
    g.destroy();
  }

  ensurePropTextures(scene);
}

// ---- props (decorative, walk-through unless noted) -------------------------

export type PropKind = 'tree' | 'pine' | 'shrub' | 'rock' | 'gravestone' | 'deadtree' | 'rubble' | 'crystal';

export interface PropStyle {
  texKey: string;
  /** If true, this prop blocks pathing. Most don't, but trees and big rocks should. */
  blocking: boolean;
  /** Render height in pixels (sprite anchor at bottom). */
  h: number;
  w: number;
}

export const PROPS: Record<PropKind, PropStyle> = {
  tree:        { texKey: 'prop_tree',       blocking: true,  h: 48, w: 36 },
  pine:        { texKey: 'prop_pine',       blocking: true,  h: 52, w: 30 },
  shrub:       { texKey: 'prop_shrub',      blocking: false, h: 18, w: 22 },
  rock:        { texKey: 'prop_rock',       blocking: true,  h: 22, w: 28 },
  gravestone:  { texKey: 'prop_gravestone', blocking: false, h: 22, w: 16 },
  deadtree:    { texKey: 'prop_deadtree',   blocking: true,  h: 46, w: 26 },
  rubble:      { texKey: 'prop_rubble',     blocking: false, h: 14, w: 24 },
  crystal:     { texKey: 'prop_crystal',    blocking: false, h: 24, w: 16 },
};

export function ensurePropTextures(scene: Phaser.Scene): void {
  for (const kind of Object.keys(PROPS) as PropKind[]) {
    const style = PROPS[kind];
    if (scene.textures.exists(style.texKey)) continue;
    const g = scene.add.graphics({ x: 0, y: 0 });
    drawProp(g, kind, style.w, style.h);
    g.generateTexture(style.texKey, style.w, style.h);
    g.destroy();
  }
}

function drawProp(g: Phaser.GameObjects.Graphics, kind: PropKind, w: number, h: number): void {
  const cx = w / 2;
  switch (kind) {
    case 'tree': {
      // Trunk
      g.fillStyle(0x4a2f17, 1);
      g.fillRect(cx - 3, h - 14, 6, 14);
      // Foliage (chunky blob)
      g.fillStyle(0x2a5a26, 1);
      g.fillCircle(cx, h - 22, 14);
      g.fillStyle(0x3a7a35, 1);
      g.fillCircle(cx - 5, h - 26, 9);
      g.fillCircle(cx + 5, h - 24, 9);
      break;
    }
    case 'pine': {
      g.fillStyle(0x4a2f17, 1);
      g.fillRect(cx - 2, h - 8, 4, 8);
      g.fillStyle(0x254a23, 1);
      // Three triangle layers.
      for (let i = 0; i < 3; i++) {
        const y = h - 10 - i * 12;
        const triW = 14 - i * 3;
        g.beginPath();
        g.moveTo(cx, y - 14);
        g.lineTo(cx - triW, y);
        g.lineTo(cx + triW, y);
        g.closePath();
        g.fillPath();
      }
      break;
    }
    case 'shrub': {
      g.fillStyle(0x3a6e2a, 1);
      g.fillCircle(cx - 4, h - 6, 6);
      g.fillCircle(cx + 4, h - 7, 6);
      g.fillStyle(0x5a8e3a, 1);
      g.fillCircle(cx, h - 10, 6);
      break;
    }
    case 'rock': {
      g.fillStyle(0x6a6a6a, 1);
      g.fillEllipse(cx, h - 8, w - 4, 14);
      g.fillStyle(0x8a8a8a, 1);
      g.fillEllipse(cx - 3, h - 12, 10, 6);
      break;
    }
    case 'gravestone': {
      g.fillStyle(0x6a6660, 1);
      g.fillRect(cx - 5, h - 18, 10, 18);
      g.fillStyle(0x4a4640, 1);
      g.fillRect(cx - 6, h - 4, 12, 4);
      // Cross.
      g.fillStyle(0x2a2620, 1);
      g.fillRect(cx - 1, h - 16, 2, 10);
      g.fillRect(cx - 3, h - 13, 6, 2);
      break;
    }
    case 'deadtree': {
      g.fillStyle(0x3a2a20, 1);
      g.fillRect(cx - 2, h - 14, 4, 14);
      // Bare branches.
      g.lineStyle(2, 0x3a2a20, 1);
      g.beginPath();
      g.moveTo(cx, h - 14); g.lineTo(cx - 10, h - 28);
      g.moveTo(cx, h - 18); g.lineTo(cx + 8, h - 32);
      g.moveTo(cx, h - 12); g.lineTo(cx - 6, h - 22);
      g.moveTo(cx, h - 16); g.lineTo(cx + 6, h - 24);
      g.strokePath();
      break;
    }
    case 'rubble': {
      g.fillStyle(0x6a6660, 1);
      g.fillRect(cx - 8, h - 6, 6, 6);
      g.fillRect(cx - 2, h - 9, 5, 9);
      g.fillRect(cx + 4, h - 5, 4, 5);
      break;
    }
    case 'crystal': {
      g.fillStyle(0x6a3aaa, 1);
      g.beginPath();
      g.moveTo(cx, h - 22); g.lineTo(cx - 5, h - 4); g.lineTo(cx + 5, h - 4);
      g.closePath(); g.fillPath();
      g.fillStyle(0x9a6aff, 1);
      g.beginPath();
      g.moveTo(cx, h - 22); g.lineTo(cx + 2, h - 4); g.lineTo(cx + 5, h - 4);
      g.closePath(); g.fillPath();
      break;
    }
  }
}
